import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import Layout from '@/components/Layout';
import { TALENTS } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const CATEGORIES = ['All', 'Pageant', 'Model', 'Commercial', 'Director'];

export default function Talents() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? TALENTS
    : TALENTS.filter(t => t.category === activeCategory);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-40 pb-20 min-h-[55vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1532581646393-e75be8f3d166?w=1600&q=85"
            alt="Our Talents"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.4)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-5">Our Roster</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,6rem)] text-[#f5f5f5] max-w-3xl">
              Faces of the <span className="text-gold italic">Ecosystem</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-[#080808] border-b border-[rgba(201,168,76,0.08)]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-center gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-[10px] tracking-[0.2em] uppercase px-5 py-2 border transition-all duration-300 ${
                  activeCategory === cat
                    ? 'border-[#c9a84c] text-[#c9a84c] bg-[rgba(201,168,76,0.08)]'
                    : 'border-[rgba(201,168,76,0.2)] text-[#f5f5f5]/50 hover:border-[rgba(201,168,76,0.5)] hover:text-[#f5f5f5]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Talents grid */}
      <section className="py-20 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((talent, i) => (
              <FadeUp key={talent.id} delay={i * 0.06}>
                <div className="group cursor-pointer">
                  <div className="card-editorial h-80 lg:h-96 mb-4">
                    <img src={talent.image} alt={talent.name} loading="lazy" />
                    <div className="card-overlay" />
                    {/* Hover detail */}
                    <div className="absolute inset-0 flex flex-col justify-end p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                      <div className="divider-gold-left mb-3" />
                      <ul className="space-y-1">
                        {talent.achievements.slice(0, 2).map((ach, j) => (
                          <li key={j} className="text-[9px] tracking-[0.12em] text-[#c9a84c]/80 uppercase">{ach}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="eyebrow text-[9px] bg-[rgba(8,8,8,0.6)] px-2 py-1">{talent.category}</span>
                    </div>
                  </div>
                  <h4 className="heading-display text-xl text-[#f5f5f5] group-hover:text-[#c9a84c] transition-colors duration-300">{talent.name}</h4>
                  <p className="text-[11px] tracking-[0.1em] text-[#f5f5f5]/45 mt-1">{talent.title}</p>
                  <p className="text-[10px] tracking-[0.1em] text-[#c9a84c]/60 uppercase mt-1">{talent.company}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
